import { contrastRatio, ratingOf } from '../../../design-tokens/contrast';
import resolved from '../../../design-tokens/generated/resolved.json';

export type CoreColors = Readonly<Record<string, string>>;

export interface RampStep {
  step: number;
  hex: string;
}

export interface BrandPrint {
  brand: Readonly<Record<string, { cmyk: string; pms?: string }>>;
}

export interface BrandSwatch {
  key: string;
  name: string;
  group: 'primary' | 'accent';
  // The white chip has no contrast against the page, so the frame outlines it with Neutral/border.
  bordered: boolean;
  cssVar: string;
  hex: string;
  rgb: string;
  cmyk: string;
  pms?: string;
}

export interface RampMeta {
  name: string;
  title: string;
  hue: string;
  base: number;
  // Replaces the "<hex> · base <step>" part of the summary (Neutral has no single base colour).
  detail?: string;
  note?: string;
}

// Order, names, hues and notes are the ones of the Palettes block of Figma 13627:142411.
export const RAMPS: readonly RampMeta[] = [
  {
    name: 'neutral',
    title: 'Neutral',
    hue: 'Grey',
    base: 500,
    detail: '50/300/500/700 = guideline greys',
  },
  { name: 'primary', title: 'Primary', hue: 'Teal', base: 500 },
  { name: 'action', title: 'Action', hue: 'Blue', base: 800 },
  { name: 'warning', title: 'Warning', hue: 'Orange', base: 500, note: 'accent only' },
  { name: 'accent', title: 'Accent', hue: 'Yellow', base: 400, note: 'accent only' },
  { name: 'success', title: 'Success', hue: 'Green', base: 500, note: 'derived to harmonise' },
  { name: 'error', title: 'Error', hue: 'Red', base: 500, note: 'derived to harmonise' },
];

const BRAND: ReadonlyArray<Pick<BrandSwatch, 'key' | 'name' | 'group' | 'bordered'>> = [
  { key: 'blue', name: 'DHCS Blue', group: 'primary', bordered: false },
  { key: 'teal', name: 'DHCS Teal', group: 'primary', bordered: false },
  { key: 'white', name: 'White', group: 'primary', bordered: true },
  { key: 'yellow', name: 'DHCS Yellow', group: 'accent', bordered: false },
  { key: 'orange', name: 'DHCS Orange', group: 'accent', bordered: false },
];

const NEUTRAL_0 = '--color-neutral-0';
const NEUTRAL_900 = '--color-neutral-900';

// Neutral 0 and 900 swap values between the modes but are the same two colours, so the pick
// made against the light pair is right in both.
const { light } = resolved.core;

// The page is read in whichever mode the root is in; `?theme=dark` (src/theme-mode.ts) sets it before render.
export function activeCore(): CoreColors {
  return document.documentElement.dataset.theme === 'dark' ? resolved.core.dark : light;
}

// The name of a Core variable, e.g. `--color-primary-500`; tokens:vars checks `var(--...)` literals, which a built name would hide.
export function coreVar(ramp: string, step: number): string {
  return `--color-${ramp}-${step}`;
}

export function rampSteps(core: CoreColors, ramp: string): RampStep[] {
  const pattern = new RegExp(`^--color-${ramp}-(\\d+)$`);

  return Object.entries(core)
    .flatMap(([name, hex]) => {
      const match = pattern.exec(name);

      return match ? [{ step: Number(match[1]), hex }] : [];
    })
    .sort((a, b) => a.step - b.step);
}

export function labelColorFor(swatch: string): string {
  const white = light[NEUTRAL_0];
  const black = light[NEUTRAL_900];

  return contrastRatio(swatch, white) > contrastRatio(swatch, black) ? white : black;
}

// The CSS variable that holds the label colour in the mode of `core`.
export function labelVarFor(swatch: string, core: CoreColors): string {
  return labelColorFor(swatch) === core[NEUTRAL_0] ? `var(${NEUTRAL_0})` : `var(${NEUTRAL_900})`;
}

export function labelContrast(swatch: string): number {
  return contrastRatio(swatch, labelColorFor(swatch));
}

export function contrastLabel(ratio: number): string {
  const rating = ratingOf(ratio);
  // 3:1 is the WCAG threshold for large text and UI components; ratingOf only knows normal text.
  const shown = rating === 'fail' && ratio >= 3 ? 'AA lg' : rating;

  return `${ratio.toFixed(2)}:1 · ${shown}`;
}

export function hexToRgbText(hex: string): string {
  const digits = hex.slice(1);

  return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16)).join('.');
}

export function describeRamp(meta: RampMeta, core: CoreColors): string {
  const baseHex = core[coreVar(meta.name, meta.base)];
  // A ramp a re-export dropped or renamed says so, rather than printing "undefined".
  if (baseHex === undefined) return `${meta.title}: missing from the Figma export`;
  const base = meta.detail ?? `${baseHex} · base ${meta.base}`;

  return [meta.hue, base, meta.note].filter(Boolean).join(' · ');
}

export function brandSwatches(core: CoreColors, print: BrandPrint): BrandSwatch[] {
  return BRAND.map((entry) => {
    const cssVar = `--color-brand-${entry.key}`;
    const hex = core[cssVar];
    // A brand colour a re-export dropped is named on the page instead of crashing it.
    if (hex === undefined)
      return {
        ...entry,
        cssVar,
        hex: 'missing from the Figma export',
        rgb: '—',
        ...print.brand[entry.key],
      };

    return { ...entry, cssVar, hex, rgb: hexToRgbText(hex), ...print.brand[entry.key] };
  });
}
